import { inventoryClearScopes } from "./inventoryTargetViews";
import { inventoryTableSx } from "./inventoryTableStyles";
import {
  PeriodLayout,
  inventoryGroupsForLayout,
  inventoryCopyScopes,
} from "./periodLayout";
import { Stack } from "@mui/material";
import { InventoryTargetScopeMenu } from "./InventoryTargetScopeMenu";
import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { NameBasedTargetInventoryItem } from "store/api/generatedApi";
import { PeriodTargetsPopover } from "./PeriodTargetsPopover";
import { InventoryTargetCell } from "./InventoryTargetCell";
import {
  InventoryPeriodGroup,
  InventoryTargetBound,
  InventoryTargetCopyScope,
  PeriodInventoryTargets,
  MaterialInventoryTargetsByPeriod,
  InventoryTargetsByPeriod,
} from "./ruleParameters";

interface InventoryPeriodRow {
  material_name: string;
  targets: MaterialInventoryTargetsByPeriod;
}

export const InventoryPeriodTable = ({
  targetsByGroup,
  resolved,
  deployable,
  previewMaterials,
  rules,
  onCopy,
  onClear,
  materials,
  onChange,
  disabled,
  periodLayout,
}: {
  targetsByGroup: Record<InventoryPeriodGroup, NameBasedTargetInventoryItem[]>;
  resolved: InventoryTargetsByPeriod;
  deployable: number;
  rules: PeriodInventoryTargets[];
  previewMaterials: ReadonlySet<string>;
  onCopy: (
    material: string,
    bound: InventoryTargetBound,
    quantity: number | null,
    scope: InventoryTargetCopyScope
  ) => void;
  materials: string[];
  onChange: (
    group: InventoryPeriodGroup,
    material: string,
    bound: InventoryTargetBound,
    quantity: number | null
  ) => void;
  onClear: (
    material: string,
    bound: InventoryTargetBound,
    scope: InventoryTargetCopyScope
  ) => void;
  disabled: boolean;
  periodLayout: PeriodLayout;
}) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);
  const groups = inventoryGroupsForLayout(periodLayout);
  const periods = groups.map(({ group }) => group);
  const singlePeriod =
    periodLayout.deployable.maximum === 1 &&
    periodLayout.forecast.maximum === 0;
  const scopes = inventoryCopyScopes(periodLayout);
  const names = [
    ...new Set([
      ...materials,
      ...Object.values(targetsByGroup).flatMap((targets) =>
        targets.map((target) => target.material_name)
      ),
    ]),
  ];
  const rows: InventoryPeriodRow[] = names.map((material_name) => ({
    material_name,
    targets: Object.fromEntries(
      periods.map((period) => [
        period,
        targetsByGroup[period].find(
          (target) => target.material_name === material_name
        ) ?? {
          material_name,
          min_quantity: null,
          max_quantity: null,
        },
      ])
    ),
  }));
  const columns: GridColDef<InventoryPeriodRow>[] = [
    {
      field: "material_name",
      headerName: t("material"),
      width: 280,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          {t(row.material_name)}
          {!disabled && previewMaterials.has(row.material_name) ? (
            <InventoryTargetScopeMenu
              scopes={scopes}
              action="clear"
              clearScopes={inventoryClearScopes({
                resolved,
                material: row.material_name,
                deployable,
              })}
              label={t("autoClearInventoryTargets", {
                material: t(row.material_name),
              })}
              onSelect={(scope, bound) => {
                onClear(row.material_name, bound, scope);
              }}
            />
          ) : null}
        </Stack>
      ),
    },
    ...(!singlePeriod
      ? [
          {
            field: "preview",
            cellClassName: "inventory-preview-cell",
            headerName: "",
            width: 50,
            disableColumnMenu: true,
            sortable: false,
            filterable: false,
            renderCell: ({ row }: { row: InventoryPeriodRow }) => (
              <PeriodTargetsPopover
                material={row.material_name}
                hasBounds={previewMaterials.has(row.material_name)}
                rules={rules}
                labelVisibility="hidden"
                periodLayout={periodLayout}
              />
            ),
          },
        ]
      : []),
    ...periods.flatMap((period) =>
      (["min_quantity", "max_quantity"] as const).map(
        (bound): GridColDef<InventoryPeriodRow> => ({
          field: `${period}/${bound}`,
          headerName: `${t(bound === "min_quantity" ? "targetMin" : "targetMax")}${units("mass")}`,
          type: "number",
          editable: !disabled,
          width: 130,
          valueGetter: (_, row) => row.targets[period]?.[bound] ?? null,
          valueSetter: (quantity: number | null, row) => ({
            ...row,
            targets: {
              ...row.targets,
              [period]: {
                material_name: row.material_name,
                min_quantity: null,
                max_quantity: null,
                ...row.targets[period],
                [bound]: quantity,
              },
            },
          }),
          renderCell: ({ value, row }) => (
            <InventoryTargetCell
              scopes={singlePeriod ? [] : scopes}
              quantity={value as number | null}
              bound={bound}
              disabled={disabled}
              onCopy={(scope) => {
                onCopy(row.material_name, bound, value as number | null, scope);
              }}
            />
          ),
        })
      )
    ),
  ];
  return (
    <HotkeyCompliantDataGrid
      sx={{
        ...inventoryTableSx,
        "& .inventory-preview-cell": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 0,
          textOverflow: "clip",
        },
      }}
      rows={rows}
      columns={columns}
      getRowId={(row) => row.material_name}
      initialState={{
        pinnedColumns: {
          left: singlePeriod ? ["material_name"] : ["material_name", "preview"],
        },
      }}
      disableRowSelectionOnClick
      columnGroupingModel={
        singlePeriod
          ? []
          : groups.map(({ group: period, label }) => ({
              groupId: period,
              headerName: t(label),
              children: [
                { field: `${period}/min_quantity` },
                { field: `${period}/max_quantity` },
              ],
            }))
      }
      processRowUpdate={(
        updated: InventoryPeriodRow,
        original: InventoryPeriodRow
      ) => {
        const changed = periods.find(
          (period) => updated.targets[period] !== original.targets[period]
        );
        const target = changed ? updated.targets[changed] : undefined;
        if (changed && target) {
          const bound = (["min_quantity", "max_quantity"] as const).find(
            (bound) => target[bound] !== original.targets[changed]?.[bound]
          );
          if (bound)
            onChange(changed, target.material_name, bound, target[bound]);
        }
        return updated;
      }}
    />
  );
};
